import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  LightbulbIcon,
  XCircleIcon,
} from 'lucide-react';
import { apiGet } from '../../api/client.js';
import { ELEARNING_BASE } from '../../pages/ELearnings.js';
import type { PracticeQuestion, PracticeResponse } from '../../pages/ELearnings.js';

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

export function StudentPractice({
  courseId,
  moduleId,
  onBack,
}: {
  courseId: string;
  moduleId: string;
  onBack: () => void;
}): JSX.Element {
  const [questions, setQuestions] = useState<PracticeQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    void apiGet<PracticeResponse>(
      `${ELEARNING_BASE}/courses/${courseId}/modules/${moduleId}/practice`,
    )
      .then((d) => setQuestions(d.questions))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [courseId, moduleId]);

  const current = currentIndex < questions.length ? questions[currentIndex] : null;

  const options = useMemo(
    () => (current ? [...current.answerOptions].sort((a, b) => a.sortOrder - b.sortOrder) : []),
    [current],
  );

  const handleCheck = useCallback(() => {
    if (selectedOption == null || !current) return;
    setShowFeedback(true);
    if (options[selectedOption]?.isCorrect) {
      setCorrectCount((c) => c + 1);
    }
  }, [selectedOption, current, options]);

  const handleNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      setFinished(true);
    }
  }, [currentIndex, questions.length]);

  const handleRestart = useCallback(() => {
    setCurrentIndex(0);
    setSelectedOption(null);
    setShowFeedback(false);
    setCorrectCount(0);
    setFinished(false);
  }, []);

  if (loading) {
    return <div className="py-16 text-center text-[13px] text-ink-900/40">Oefenvragen laden…</div>;
  }

  if (loadError || questions.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-5 py-12">
        <div className="rounded-2xl border border-dashed border-brand-200 py-14 text-center">
          <LightbulbIcon size={28} className="mx-auto mb-3 text-ink-900/20" />
          <p className="text-[14px] font-medium text-ink-900/50">
            {loadError
              ? 'De oefenvragen konden niet worden geladen.'
              : 'Er zijn geen oefenvragen voor dit onderdeel.'}
          </p>
          <button
            type="button"
            onClick={onBack}
            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:underline"
          >
            <ArrowLeftIcon size={14} />
            Terug naar de cursus
          </button>
        </div>
      </div>
    );
  }

  // ─── Afgerond ────────────────────────────────────────────────────────────────

  if (finished) {
    const percentage =
      questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
    return (
      <div className="mx-auto max-w-2xl px-5 py-8">
        <div className="rounded-2xl border border-brand-100 bg-white p-8 text-center shadow-sm">
          <CheckCircle2Icon size={56} className="mx-auto mb-4 text-brand-500" />
          <h2 className="text-[22px] font-bold text-ink-900">Oefening voltooid</h2>
          <p className="mt-2 text-[14px] text-ink-900/65">
            Je hebt{' '}
            <span className="font-semibold text-ink-900">
              {correctCount} van {questions.length}
            </span>{' '}
            vragen goed beantwoord ({percentage}%).
          </p>
          <p className="mt-1 text-[12px] text-ink-900/45">
            Oefenen telt niet mee voor je voortgang of examen.
          </p>

          <div className="mx-auto mt-5 h-3 w-full max-w-xs overflow-hidden rounded-full bg-brand-100">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                percentage >= 60 ? 'bg-green-500' : 'bg-mosterdgeel-500'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>

          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleRestart}
              className="rounded-xl border border-brand-200 bg-white px-4 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50"
            >
              Opnieuw oefenen
            </button>
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
            >
              <ArrowLeftIcon size={15} />
              Terug naar de cursus
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Vraag ───────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-2xl px-5 py-8">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-[13px] text-ink-900/55">
        <button type="button" onClick={onBack} className="hover:text-brand-700 hover:underline">
          Cursus
        </button>
        <ChevronRightIcon size={13} />
        <span className="font-medium text-ink-900">Oefenen</span>
      </nav>

      <div className="mb-3 flex items-center justify-between">
        <span className="text-[13px] font-medium text-ink-900">
          Vraag {currentIndex + 1} van {questions.length}
        </span>
        <span className="text-[11px] text-ink-900/50">{correctCount} goed</span>
      </div>

      <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-brand-100">
        <div
          className="h-full rounded-full bg-brand-500 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
        />
      </div>

      {current && (
        <div className="rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
          <h2 className="text-[15px] font-bold leading-relaxed text-ink-900">{current.title}</h2>
          {current.questionText && (
            <p className="mt-2 text-[13px] text-ink-900/65">{current.questionText}</p>
          )}

          <div className="mb-6 mt-4 space-y-3">
            {options.map((option, index) => {
              const isSelected = selectedOption === index;
              const showCorrect = showFeedback && option.isCorrect;
              const showWrong = showFeedback && isSelected && !option.isCorrect;

              let borderClass = 'border-brand-100 hover:border-brand-300';
              if (showCorrect) borderClass = 'border-green-500 bg-green-50';
              else if (showWrong) borderClass = 'border-red-500 bg-red-50';
              else if (isSelected && !showFeedback) borderClass = 'border-brand-500 bg-brand-50';

              return (
                <button
                  key={index}
                  type="button"
                  disabled={showFeedback}
                  onClick={() => setSelectedOption(index)}
                  className={`flex w-full items-start gap-3 rounded-lg border-2 p-3.5 text-left transition-colors ${borderClass} ${
                    showFeedback ? 'cursor-default' : 'cursor-pointer'
                  }`}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${
                      showCorrect
                        ? 'bg-green-500 text-white'
                        : showWrong
                          ? 'bg-red-500 text-white'
                          : isSelected
                            ? 'bg-brand-600 text-white'
                            : 'bg-brand-50 text-ink-900/60'
                    }`}
                  >
                    {OPTION_LETTERS[index]}
                  </span>
                  <span className="flex-1 pt-0.5 text-[13px] text-ink-900">{option.text}</span>
                  {showCorrect && (
                    <CheckCircle2Icon size={18} className="mt-0.5 shrink-0 text-green-500" />
                  )}
                  {showWrong && <XCircleIcon size={18} className="mt-0.5 shrink-0 text-red-500" />}
                </button>
              );
            })}
          </div>

          {/* Uitleg na feedback */}
          {showFeedback && current.explanation && (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-petrol-100 bg-petrol-50 p-4">
              <LightbulbIcon size={18} className="mt-0.5 shrink-0 text-petrol-500" />
              <div>
                <p className="text-[13px] font-semibold text-ink-900">Toelichting</p>
                <p className="mt-1 text-[13px] text-ink-900/70">{current.explanation}</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-900/55 hover:text-ink-900"
            >
              <ArrowLeftIcon size={15} />
              Stoppen
            </button>

            {!showFeedback ? (
              <button
                type="button"
                disabled={selectedOption == null}
                onClick={handleCheck}
                className="rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Controleer antwoord
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
              >
                {currentIndex < questions.length - 1 ? (
                  <>
                    Volgende vraag
                    <ChevronRightIcon size={15} />
                  </>
                ) : (
                  'Bekijk resultaat'
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
